import type { ReactNode } from "react";

import clsx from "clsx";
import { createContext, useContext, useState } from "react";

import { Typo } from "../Typo";

export type TabId = string | number | null;

export type TabsVariant = "default" | "navigation";

export type TabsControllerProps = {
  children: ReactNode;
  activeTab: TabId;
  onChange?: (tabId: TabId) => void;
};

type TabsContextType = {
  activeTab: TabId;
  setActiveTab: (tabId: TabId) => void;
};

export type Tab = {
  id: TabId;
  label: string;
  tabStyles?: string;
};

export type TabsProps = {
  tabs: Tab[];
  containerClassNames?: string;
  tabClassNames?: string;
  scrollable?: boolean;
  activeTabClassNames?: string;
  inactiveTabClassNames?: string;
  fitContent?: boolean;
  variant?: TabsVariant;
};

export type TabContentProps = {
  id: TabId;
  children: ReactNode;
};

const TabsContext = createContext<TabsContextType | undefined>(undefined);

const TabsController = ({
  children,
  activeTab: externalActiveTab,
  onChange,
}: TabsControllerProps) => {
  const [internalActiveTab, setInternalActiveTab] = useState<TabId>(
    externalActiveTab || null,
  );
  const activeTab =
    externalActiveTab !== undefined ? externalActiveTab : internalActiveTab;

  const setActiveTab = (tabId: TabId) => {
    if (onChange) {
      onChange(tabId);
    } else {
      setInternalActiveTab(tabId);
    }
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className="w-full">{children}</div>
    </TabsContext.Provider>
  );
};

const useTabsContext = (): TabsContextType => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("useTabsContext must be used within a TabsController");
  }
  return context;
};

const Tabs = ({
  tabs,
  containerClassNames = "",
  tabClassNames = "",
  scrollable = false,
  activeTabClassNames,
  inactiveTabClassNames,
  fitContent = false,
  variant = "default",
}: TabsProps) => {
  const { activeTab, setActiveTab } = useTabsContext();

  if (variant === "navigation") {
    const navigationMarkup = (
      <div
        className={clsx(
          "flex items-center gap-4 border-b border-stroke-primary",
          scrollable && "w-full min-w-max",
          containerClassNames,
        )}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                "flex shrink-0 flex-col items-start justify-end gap-1 pt-2",
                tab.tabStyles || tabClassNames,
                isActive ? activeTabClassNames : inactiveTabClassNames,
              )}
            >
              <Typo
                element="span"
                type="labelSM"
                textColor={
                  isActive ? "text-text-primary" : "text-text-tertiary"
                }
                className="whitespace-nowrap"
              >
                {tab.label}
              </Typo>
              <span
                aria-hidden="true"
                className={clsx(
                  "h-[2px] w-full rounded-full",
                  isActive ? "bg-surface-brand-default" : "bg-transparent",
                )}
              />
            </button>
          );
        })}
      </div>
    );

    if (scrollable) {
      return (
        <div className="hide-scrollbar overflow-x-auto">{navigationMarkup}</div>
      );
    }

    return navigationMarkup;
  }

  const tabsMarkup = (
    <div
      className={`flex border-b border-grey-125 ${
        scrollable ? "w-full min-w-max" : ""
      } ${containerClassNames}`}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => setActiveTab(tab.id)}
          className={`-mb-[2px] ${
            scrollable || fitContent
              ? "flex-shrink-0 whitespace-nowrap"
              : "flex-1"
          } border-b-2 py-2 text-center font-semibold transition-all ${
            activeTab === tab.id
              ? `border-border-blue-base text-text-primary ${activeTabClassNames ?? ""}`
              : `border-transparent text-light-product-text-subdued ${inactiveTabClassNames ?? ""}`
          } ${scrollable ? "" : `${tab.tabStyles || tabClassNames}`}`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  if (scrollable) {
    return (
      <div className="hide-scrollbar -mb-[2px] overflow-x-auto pb-[2px]">
        {tabsMarkup}
      </div>
    );
  }

  return tabsMarkup;
};

const TabContent = ({ id, children }: TabContentProps) => {
  const { activeTab } = useTabsContext();
  return activeTab === id ? (
    <div className="rounded-b-lg bg-white">{children}</div>
  ) : null;
};

const TabsComponent = {
  Controller: TabsController,
  Tabs,
  Content: TabContent,
};

export { TabsComponent };
